import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';
import GeneratedPaper from '../PaperGenerator/GeneratedPaper';
import { useTheme } from '../../contexts/ThemeContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const { FiFileText, FiDownload, FiEye, FiTrash2, FiCalendar, FiClock, FiHash, FiFilter, FiAlertCircle, FiRefreshCw, FiCheck, FiSearch } = FiIcons;

const GeneratedPapers = () => {
  const [papers, setPapers] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  
  const { darkMode } = useTheme();
  const paperRef = useRef(null);

  useEffect(() => {
    loadPapers();
  }, []);

  const loadPapers = () => {
    setPapers(storage.getPapers());
  };

  const filteredPapers = papers.filter(paper => {
    // Apply search query filter
    const searchMatch = !searchQuery || 
      paper.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      paper.courseCode?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply category filter
    if (filterBy === 'all') return searchMatch;
    if (filterBy === 'recent') {
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return searchMatch && new Date(paper.createdAt) > oneWeekAgo;
    }
    return searchMatch && paper.generationMethod === filterBy;
  });

  const sortedPapers = [...filteredPapers].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.generatedAt || b.createdAt) - new Date(a.generatedAt || a.createdAt);
      case 'oldest':
        return new Date(a.generatedAt || a.createdAt) - new Date(b.generatedAt || b.createdAt);
      case 'title':
        return (a.title || '').localeCompare(b.title || '');
      case 'subject':
        return (a.subject || '').localeCompare(b.subject || '');
      case 'marks':
        return (b.totalMarks || 0) - (a.totalMarks || 0);
      default:
        return 0;
    }
  });

  const handleDeletePaper = (paperId) => {
    storage.deletePaper(paperId);
    loadPapers();
  };

  const handleDeleteAll = () => {
    setIsDeleting(true);
    try {
      // Get all papers
      const allPapers = storage.getPapers();
      
      // Delete each paper
      allPapers.forEach(paper => {
        storage.deletePaper(paper.id);
      });
      
      // Reload papers (should be empty now)
      setPapers([]);
      setShowConfirmation(false);
    } catch (error) {
      console.error('Error deleting papers:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewPaper = (paper) => {
    setSelectedPaper(paper);
  };

  const handleDownloadPDF = async (paper) => {
    setIsPdfGenerating(true);
    
    try {
      // First view the paper to render it
      setSelectedPaper(paper);
      
      // Wait for the paper to render
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (paperRef.current) {
        // Store current background color
        const originalBg = document.body.style.backgroundColor;
        // Set background to white for PDF generation
        document.body.style.backgroundColor = 'white';
        
        const canvas = await html2canvas(paperRef.current, {
          scale: 2, // Higher quality
          useCORS: true, // To handle external images
          logging: false,
          backgroundColor: '#ffffff',
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        
        // Calculate dimensions
        const imgWidth = 210; // A4 width in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        
        // Restore original background
        document.body.style.backgroundColor = originalBg;
        
        // Save the PDF
        pdf.save(`${paper.title || 'Exam_Paper'}.pdf`);
        
        // Go back to the papers list
        setSelectedPaper(null);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // If viewing a specific paper
  if (selectedPaper) {
    return (
      <GeneratedPaper 
        paper={selectedPaper} 
        onBack={() => setSelectedPaper(null)} 
      />
    );
  }

  return (
    <div className="min-h-screen">
      <div className="space-y-6 p-8 ml-72">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Generated Papers</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1">View, print and manage your generated exam papers</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search papers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <SafeIcon 
                icon={FiSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" 
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Title A-Z</option>
              <option value="subject">Subject A-Z</option>
              <option value="marks">Marks (High-Low)</option>
            </select>
          </div>
        </div>

        {/* Filter Options */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterBy('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filterBy === 'all'
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All Papers
          </button>
          <button
            onClick={() => setFilterBy('recent')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filterBy === 'recent'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Recent (7 days)
          </button>
          <button
            onClick={() => setFilterBy('quick-gen')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filterBy === 'quick-gen'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            Quick Generated
          </button>
        </div>

        {/* Papers List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              Papers ({sortedPapers.length})
            </h3>
            
            {papers.length > 0 && (
              <button
                onClick={() => setShowConfirmation(true)}
                className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center space-x-1"
              >
                <SafeIcon icon={FiTrash2} className="text-red-500" />
                <span>Delete All</span>
              </button>
            )}
          </div>
          
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedPapers.length === 0 ? (
              <div className="p-12 text-center">
                <SafeIcon icon={FiFileText} className="mx-auto text-4xl text-gray-300 dark:text-gray-600 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No papers generated yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Generate your first exam paper to see it here
                </p>
              </div>
            ) : (
              <AnimatePresence>
                {sortedPapers.map((paper, index) => (
                  <motion.div
                    key={paper.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-800 dark:text-white mb-2">{paper.title}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {paper.subject && (
                            <div className="flex items-center space-x-1">
                              <SafeIcon icon={FiFileText} className="text-xs" />
                              <span>{paper.subject}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiClock} className="text-xs" />
                            <span>{formatDuration(paper.duration)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiHash} className="text-xs" />
                            <span>{paper.totalMarks} marks</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} className="text-xs" />
                            <span>{new Date(paper.generatedAt || paper.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm">
                          {paper.courseCode && (
                            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full">
                              {paper.courseCode}
                            </span>
                          )}
                          <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-1 rounded-full">
                            {paper.questions?.length || 0} questions
                          </span>
                          {paper.generationMethod === 'quick-gen' && (
                            <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded-full">
                              Quick Gen
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button 
                          onClick={() => handleViewPaper(paper)}
                          className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                          title="View Paper"
                        >
                          <SafeIcon icon={FiEye} />
                        </button>
                        <button 
                          onClick={() => handleDownloadPDF(paper)}
                          className="p-2 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                          title="Download PDF"
                          disabled={isPdfGenerating}
                        >
                          <SafeIcon icon={isPdfGenerating ? FiRefreshCw : FiDownload} className={isPdfGenerating ? "animate-spin" : ""} />
                        </button>
                        <button
                          onClick={() => handleDeletePaper(paper.id)}
                          className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete Paper"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* Delete All Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center space-x-3 text-red-600 dark:text-red-400 mb-4">
                <SafeIcon icon={FiAlertCircle} className="text-2xl" />
                <h3 className="text-xl font-bold">Confirm Deletion</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete all generated papers? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-1"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiTrash2} />
                      <span>Delete All</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GeneratedPapers;